/**
 * Dashboard API functions
 * Connects React UI to FastAPI backend for BigQuery data
 * Uses apiFetch (Bearer token, or plain fetch when REACT_APP_SKIP_AUTH=true for local dev).
 */
import { apiFetch } from './authApi';
import { getApiBaseUrl } from './apiBase';
import { trackAiChatSend } from '../analytics/appAnalytics';
import { beginWork, endWork } from '../utils/appUpdate';
import { clearStoresCache } from './storesApi';

const API_BASE_URL = getApiBaseUrl();

const STORES_TTL_MS = 5 * 60 * 1000; // 5 min
let _storesPromise = null;
let _storesTs = 0;

/** Clear dashboard caches on logout (multi-tenant: prevent user B seeing user A's data) */
export function clearDashboardCache() {
  _storesPromise = null;
  _storesTs = 0;
  _monthsPromise = null;
  _monthsTs = 0;
  _chatSessionsPromise = null;
  _chatSessionsTs = 0;
  clearStoresCache(); // 店舗タブ（店舗サマリ・人数）のキャッシュも消す
}

/**
 * Fetch all stores for the configured client
 * @returns {Promise<{client: string, stores: string[], count: number}>}
 */
export async function fetchStores() {
  const now = Date.now();
  if (!_storesPromise || now - _storesTs > STORES_TTL_MS) {
    _storesPromise = (async () => {
      const response = await apiFetch(`${API_BASE_URL}/dashboard/stores`);
      if (!response.ok) throw new Error(`Failed to fetch stores: ${response.statusText}`);
      return response.json();
    })();
    _storesTs = now;
  }
  return _storesPromise;
}

/**
 * Fetch stylists, optionally filtered by store
 * @param {string|null} store - Optional store name to filter by
 * @returns {Promise<{client: string, store: string|null, stylists: string[], count: number}>}
 */
export async function fetchStylists(store = null) {
  const params = new URLSearchParams();
  if (store) params.append('store', store);
  
  const url = `${API_BASE_URL}/dashboard/stylists${params.toString() ? '?' + params.toString() : ''}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Failed to fetch stylists: ${response.statusText}`);
  return response.json();
}

const MONTHS_TTL_MS = 5 * 60 * 1000; // 5 min
let _monthsPromise = null;
let _monthsTs = 0;

/**
 * Fetch available months
 * @returns {Promise<{client: string, months: string[], count: number}>}
 */
export async function fetchMonths() {
  const now = Date.now();
  if (!_monthsPromise || now - _monthsTs > MONTHS_TTL_MS) {
    _monthsPromise = (async () => {
      const response = await apiFetch(`${API_BASE_URL}/dashboard/months`);
      if (!response.ok) throw new Error(`Failed to fetch months: ${response.statusText}`);
      return response.json();
    })();
    _monthsTs = now;
  }
  return _monthsPromise;
}

/**
 * Fetch KPI data with optional filters (stylist-level)
 * @param {Object} filters - Filter options
 * @param {string|null} filters.store - Store name
 * @param {string|null} filters.stylist - Stylist name
 * @param {string|null} filters.month - Month in YYYY-MM format
 * @returns {Promise<{filters: Object, kpis: Object}>}
 */
export async function fetchKPIs({ store = null, stylist = null, month = null } = {}) {
  const params = new URLSearchParams();
  if (store) params.append('store', store);
  if (stylist) params.append('stylist', stylist);
  if (month) params.append('month', month);
  
  const url = `${API_BASE_URL}/dashboard/kpis${params.toString() ? '?' + params.toString() : ''}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Failed to fetch KPIs: ${response.statusText}`);
  return response.json();
}

/**
 * Fetch store-wide average KPIs (averaged across all stylists)
 * @param {Object} filters - Filter options
 * @param {string|null} filters.store - Store name
 * @param {string|null} filters.month - Month in YYYY-MM format
 * @returns {Promise<{filters: Object, kpis: Object}>}
 */
export async function fetchStoreAverageKPIs({ store = null, month = null } = {}) {
  const params = new URLSearchParams();
  if (store) params.append('store', store);
  if (month) params.append('month', month);
  
  const url = `${API_BASE_URL}/dashboard/kpis/store-average${params.toString() ? '?' + params.toString() : ''}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Failed to fetch store average KPIs: ${response.statusText}`);
  return response.json();
}

/**
 * Fetch monthly KPI history for graph display
 * @param {Object} filters - Filter options
 * @param {string|null} filters.store - Store name
 * @param {string|null} filters.stylist - Stylist name
 * @returns {Promise<{filters: Object, history: Array, count: number}>}
 */
export async function fetchKPIHistory({ store = null, stylist = null } = {}) {
  const params = new URLSearchParams();
  if (store) params.append('store', store);
  if (stylist) params.append('stylist', stylist);
  
  const url = `${API_BASE_URL}/dashboard/kpis/history${params.toString() ? '?' + params.toString() : ''}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Failed to fetch KPI history: ${response.statusText}`);
  return response.json();
}

/**
 * Fetch ML-generated forecasts for graph display
 * @param {Object} filters - Filter options
 * @param {string|null} filters.store - Store name
 * @param {string|null} filters.stylist - Stylist name
 * @returns {Promise<{filters: Object, forecasts: Array, count: number}>}
 */
export async function fetchMLForecasts({ store = null, stylist = null } = {}) {
  const params = new URLSearchParams();
  if (store) params.append('store', store);
  if (stylist) params.append('stylist', stylist);
  
  const url = `${API_BASE_URL}/dashboard/forecasts${params.toString() ? '?' + params.toString() : ''}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Failed to fetch ML forecasts: ${response.statusText}`);
  return response.json();
}

/**
 * Fetch AI-generated comments: monthly sections 1-3 + the home card for one day.
 *
 * Sections 1-3 are cached by month, the home card (section 4) by day — pass the
 * JST date so a card generated this morning is reused instead of regenerated.
 *
 * @param {Object} params - Required parameters
 * @param {string} params.store - Store name (required)
 * @param {string} params.stylist - Stylist name (required)
 * @param {string} params.month - Month in YYYY-MM format (required)
 * @param {string} [params.date] - Home-card day (YYYY-MM-DD). Server defaults to today in JST.
 * @returns {Promise<{success: boolean, ai_comment: string, date: string, cached: boolean}>}
 */
export async function fetchAIComment({ store, stylist, month, date = null }) {
  const params = new URLSearchParams({ store, stylist, month });
  if (date) params.append('date', date);
  const url = `${API_BASE_URL}/dashboard/ai-comment?${params}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`Failed to fetch AI comment: ${response.statusText}`);
  return response.json();
}

const CHAT_CREATE_TIMEOUT_MS = 15_000;

/**
 * AI chat readiness (Vertex env + session backend). No BQ probe.
 */
export async function fetchChatHealth({ signal } = {}) {
  const response = await apiFetch(`${API_BASE_URL}/ai/chat/health`, { signal });
  if (!response.ok) {
    throw new Error(`AI health check failed: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Create a new AI chat session (Agent Engine Sessions).
 */
export async function createChatSession({ store, stylist, month, kpi_summary, signal } = {}) {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onAbort, { once: true });
  }
  const timer = setTimeout(() => controller.abort(), CHAT_CREATE_TIMEOUT_MS);
  try {
    const body = { store, stylist, month };
    if (kpi_summary) body.kpi_summary = kpi_summary;
    const response = await apiFetch(`${API_BASE_URL}/ai/chat/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Failed to create chat session: ${response.statusText}`);
    }
    invalidateChatSessionsCache();
    return response.json();
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new Error('セッション作成がタイムアウトしました。接続を確認して再送信してください');
    }
    const msg = String(err?.message || err || '');
    if (/Failed to fetch|NetworkError|ECONNREFUSED|Load failed/i.test(msg)) {
      throw new Error('バックエンドに接続できません。localhost:8000 が起動しているか確認してください');
    }
    throw err;
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

/**
 * List AI chat sessions for the current user (optionally scoped to store/stylist).
 */
const CHAT_SESSIONS_TTL_MS = 60 * 1000;
let _chatSessionsCacheKey = null;
let _chatSessionsPromise = null;
let _chatSessionsTs = 0;

export function invalidateChatSessionsCache() {
  _chatSessionsPromise = null;
  _chatSessionsTs = 0;
  _chatSessionsCacheKey = null;
}

export async function fetchChatSessions({
  limit = 50,
  force = false,
  store = null,
  stylist = null,
} = {}) {
  const cacheKey = `${limit}|${store || ''}|${stylist || ''}`;
  const now = Date.now();
  if (
    force ||
    !_chatSessionsPromise ||
    _chatSessionsCacheKey !== cacheKey ||
    now - _chatSessionsTs > CHAT_SESSIONS_TTL_MS
  ) {
    _chatSessionsCacheKey = cacheKey;
    _chatSessionsPromise = (async () => {
      const params = new URLSearchParams();
      if (limit) params.append('limit', String(limit));
      if (store) params.append('store', store);
      if (stylist) params.append('stylist', stylist);
      const url = `${API_BASE_URL}/ai/chat/sessions${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiFetch(url);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to fetch chat sessions: ${response.statusText}`);
      }
      return response.json();
    })();
    _chatSessionsTs = now;
  }
  return _chatSessionsPromise;
}

/**
 * Get a single chat session with messages.
 */
export async function fetchChatSession(sessionId) {
  const response = await apiFetch(`${API_BASE_URL}/ai/chat/sessions/${encodeURIComponent(sessionId)}`);
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to fetch chat session: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Send a message and receive assistant reply via SSE stream.
 * Optional kpi_summary refreshes the session's home BQ KPI snapshot for the agent.
 */
export async function postChatMessageStream(sessionId, content, { onDelta, onStatus, signal, kpi_summary } = {}) {
  const body = { content };
  if (kpi_summary) body.kpi_summary = kpi_summary;
  const response = await apiFetch(
    `${API_BASE_URL}/ai/chat/sessions/${encodeURIComponent(sessionId)}/messages/stream`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(body),
      signal,
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to send chat message: ${response.statusText}`);
  }

  trackAiChatSend();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let donePayload = null;

  // apiFetch released its in-flight mark when the headers arrived; the stream is still
  // running, so hold the app-update reload until the reply is complete.
  beginWork();
  try {
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const block of parts) {
      const lines = block.split('\n');
      let eventType = 'message';
      let dataLine = '';
      for (const line of lines) {
        if (line.startsWith('event:')) eventType = line.slice(6).trim();
        if (line.startsWith('data:')) dataLine = line.slice(5).trim();
      }
      if (!dataLine) continue;
      const payload = JSON.parse(dataLine);
      if (eventType === 'delta' && payload.text) {
        onDelta?.(payload.text);
      } else if (eventType === 'status' && payload.text) {
        // the server is running a tool (POS lookup); nothing to append, just show the wait
        onStatus?.(payload.text);
      } else if (eventType === 'error') {
        throw new Error(payload.detail || 'メッセージの送信に失敗しました');
      } else if (eventType === 'done') {
        donePayload = payload;
      }
    }
  }
  } finally {
    endWork();
  }

  if (!donePayload?.assistant_message) {
    throw new Error('AIの応答に失敗しました');
  }
  invalidateChatSessionsCache();
  return donePayload;
}

/**
 * Send a message and receive assistant reply (JSON fallback).
 */
export async function postChatMessage(sessionId, content, { kpi_summary } = {}) {
  const body = { content };
  if (kpi_summary) body.kpi_summary = kpi_summary;
  const response = await apiFetch(`${API_BASE_URL}/ai/chat/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to send chat message: ${response.statusText}`);
  }
  invalidateChatSessionsCache();
  return response.json();
}

/**
 * 👍 / 👎 on one AI reply. Re-rating the same reply overwrites it on the server.
 * Task: githubDOCS/TASKS/2026-10-27_AI相談の回答に評価ボタン.md
 */
export async function postChatFeedback(sessionId, { rating, reasons = [], comment = '', reply, question = '' }) {
  const response = await apiFetch(`${API_BASE_URL}/ai/chat/sessions/${encodeURIComponent(sessionId)}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rating, reasons, comment, reply, question }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || '評価を保存できませんでした');
  }
  return response.json();
}

